import { Mail } from "lucide-react";

import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Button, buttonVariants } from "@krea/shared/design-system/Button";
import { AppModal } from "@krea/shared/design-system/modal";
import { Separator } from "@krea/shared/design-system/Separator";
import type { CountryCode } from "@krea/shared/types/common";

import type { IOfferAttractivenessTrialState } from "./trial";
import { formatDaysLeft, formatTrialEndDate } from "./trial";
import { getOfferAttractivenessContactEmail } from "./utils";

const KEEP_EMAIL_SUBJECT = "Keep Offer Attractiveness after free trial";

interface KeepOfferAttractivenessModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  trial: IOfferAttractivenessTrialState;
  countryCode: CountryCode | undefined;
}

const KeepOfferAttractivenessModal = ({
  open,
  setOpen,
  trial,
  countryCode,
}: KeepOfferAttractivenessModalProps) => {
  const email = getOfferAttractivenessContactEmail(countryCode);
  const onCloseModal = () => setOpen(false);

  return (
    <AppModal
      open={open}
      setOpen={setOpen}
      onClose={onCloseModal}
      title="Keep Offer Attractiveness"
      footer={
        <Button variant="secondary" onClick={onCloseModal}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <Alert variant="warning">
          <Alert.Title>
            {trial.endsOn
              ? `Your free trial ends ${formatTrialEndDate(trial.endsOn)}`
              : "Your free trial is ending"}
          </Alert.Title>
          <Alert.Description>
            {formatDaysLeft(trial.daysRemaining)}. Contact us before then to
            keep the score and recommendations on your offers.
          </Alert.Description>
        </Alert>

        <p className="mb-0 text-base-strong">What you keep</p>
        <ul className="mb-0 list-disc space-y-1.5 pl-5 text-sm text-black">
          <li>See a score for how attractive your offer is to customers</li>
          <li>Understand how likely your offer is to convert</li>
          <li>Get tailored recommendations to improve your offer</li>
        </ul>

        <Separator />

        <h3 className="text-section">Contact us to keep access</h3>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-150">
            <Mail className="size-4 text-primary" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <p className="mb-1 text-label">E-mail</p>
            <p className="mb-0 break-all text-base-strong">{email}</p>
          </div>
          {/* Styled as a button rather than wrapped in one: nesting an <a> inside the
              design-system <Button> puts an interactive element inside another, which is
              invalid HTML and gives keyboard and screen-reader users two targets. */}
          <a
            href={`mailto:${email}?subject=${encodeURIComponent(KEEP_EMAIL_SUBJECT)}`}
            className={buttonVariants()}
          >
            <Mail className="size-4" />
            <span>Send Email</span>
          </a>
        </div>

        <Separator />
      </div>
    </AppModal>
  );
};

export { KeepOfferAttractivenessModal };
